// EN mirror of /blog — content hub for expat-focused legal & translation articles
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, ArrowRight } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { BLOG_INDEX_EN } from "@/data/blog-index.en.gen";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Blog — Thailand Legal, Translation & Visa Guides | NYC Language Institute";
const DESC =
  "Practical guides for expats living, working and marrying in Thailand. Apostille walkthroughs. Get your timeline and checklist in minutes — chat now.";

import { blogCover } from "@/data/blog-covers";

const POSTS = BLOG_INDEX_EN.map((p) => ({
  slug: p.slug,
  title: p.title,
  category: p.category,
  read: `${p.readMinutes} min`,
}));

export const Route = createFileRoute("/en/blog/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/blog", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/blog", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/blog", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/blog", "en")},
      { property: "og:url", content: `${SITE}/en/blog` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/blog` },
      ...hreflangLinks("/blog", "/en/blog"),
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Blog",
        name: TITLE,
        url: `${SITE}/en/blog`,
        inLanguage: "en",
        publisher: { "@type": "Organization", name: "NYC Legal & Notary Service Co., Ltd.", url: SITE },
      }),
    }],
  }),
  component: EnBlogPage,
});

function EnBlogPage() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-16 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Thailand legal & paperwork <span className="text-amber-300">guides for expats</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            Long-form walkthroughs written by our notarial attorneys and sworn translators — no fluff, just the steps,
            fees, and embassy-specific quirks you need to get accepted on first submission.
          </p>
        </div>
      </section>
      <section className="px-4 py-14">
        <div className="mx-auto grid max-w-5xl gap-5 sm:grid-cols-2">
          {POSTS.map((p) => (
            <Link key={p.slug} to="/en/blog/$slug" params={{ slug: p.slug }}
              className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm hover:border-amber-400">
              <img
                src={blogCover(p.category, "en").src}
                alt={blogCover(p.category, "en").altEn}
                width={1200}
                height={630}
                loading="lazy"
                decoding="async"
                className="aspect-[1200/630] w-full object-cover"
              />
              <div className="p-6">
              <BookOpen className="h-5 w-5 text-amber-600" />
              <div className="mt-2 text-[10px] font-bold uppercase tracking-widest text-amber-600">{p.category}</div>
              <h2 className="mt-2 text-lg font-bold text-navy-deep group-hover:text-amber-700">{p.title}</h2>
              <p className="mt-2 text-xs text-slate-500">{p.read} read</p>
              <p className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-amber-700">
                Read article <ArrowRight className="h-3 w-3" />
              </p>
              </div>
            </Link>
          ))}
        </div>
        <div className="mx-auto mt-10 max-w-5xl rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-700">
          Looking for our Thai-language articles? Visit{" "}
          <Link to="/blog" className="font-bold text-amber-700 hover:underline">/blog</Link>{" "}
          for the full archive (50+ posts).
        <RelatedHubGrid locale="en" />
      </div>
      </section>
    </main>
  );
}
